import { Button, Form, Input, message, Select } from "antd"
import Title from "antd/es/typography/Title"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { t } from "~/languages/i18n"
import userService from "../services/userService"

const UpsertForm = ({ editMode = false }: { editMode?: boolean }) => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const userServices = userService()
  const [roles, setRoles] = useState([])

  const getRoles = async () => {
    try {
      const response = await userServices.fetchAllRoles()

      const filteredRoles = response.roles.filter((role: any) => role.type !== "public")

      setRoles(filteredRoles || [])
    } catch (error) {
      message.error("Failed to fetch roles.")
    }
  }

  const handleEmailBlur = async () => {
    const email = form.getFieldValue("email")
    if (email) {
      const emailExists = await userServices.checkEmailExists(email)
      if (emailExists.length > 0) {
        message.error(t("Email already exists."))
        form.setFields([
          {
            name: "email",
            errors: [t("Email already exists")],
          },
        ])
      }
    }
  }

  const handleUsernameBlur = async () => {
    const userName = form.getFieldValue("username")

    if (userName) {
      const usernameExists = await userServices.checkUsernameExists(userName)

      if (usernameExists.length > 0) {
        message.error(t("Account name already exists."))
        form.setFields([
          {
            name: "username",
            errors: [t("Account name already exists")],
          },
        ])
      }
    }
  }

  const handleFormSubmit = async (values: any) => {
    try {
      if (editMode) {
        await userServices.updateUser(Number(id), values)
      } else {
        const res = await userServices.createUser(values)
        if (res?.status === 201) {
          message.success(t("The user has been added successfully."))
        }
      }
      form.resetFields()
      navigate("/users")
    } catch (error) {
      console.error("Error during form submission:", error)
      message.error(t("An error occurred while submitting the form. Please try again."))
    }
  }

  const fetchUserData = async (userId: number) => {
    try {
      const userDataFetch = await userServices.getUserById(userId)

      if (userDataFetch) {
        form.setFieldsValue(userDataFetch)
        form.setFieldsValue({ role: userDataFetch.role.id })
        form.setFieldsValue({ language: userDataFetch.language || "en" })
      } else {
        message.warning(t("No user data found."))
      }
    } catch (error) {
      message.error(t("An error occurred while fetching user data."))
    }
  }

  useEffect(() => {
    if (editMode === false) {
      form.resetFields()
    }
  }, [editMode])

  useEffect(() => {
    if (id) {
      fetchUserData(Number(id))
    }
    getRoles()
  }, [id])

  return (
    <>
      <Title level={2} className="pb-5 p-4">
        {editMode ? t("Edit User") : t("Add New User")}
      </Title>
      <Form
        form={form}
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 14 }}
        layout="horizontal"
        style={{ maxWidth: 800 }}
        onFinish={handleFormSubmit}
        initialValues={!editMode ? { role: 3, language: "en", emailSubscription: true } : {}}
        className="p-4 ml-10"
      >
        <Form.Item
          className="font-bold"
          label="Username"
          name="username"
          rules={[
            { required: true, message: t("Account name cannot be left blank") },
            { pattern: /^[a-zA-Z0-9]+$/, message: t("The account name cannot contain accents or special characters") },
            { min: 6, message: t("Account name must be at least 6 characters") },
          ]}
        >
          <Input onBlur={handleUsernameBlur} autoComplete="current-username" />
        </Form.Item>

        <Form.Item
          className="font-bold"
          label={t("Email")}
          name="email"
          rules={[
            { required: true, message: t("Email cannot be left blank") },
            { type: "email", message: t("Email is not in correct format") },
          ]}
        >
          <Input onBlur={handleEmailBlur} />
        </Form.Item>

        <Form.Item
          className="font-bold"
          label={t("First Name")}
          name="firstName"
          rules={[{ required: true, message: t("FirstName cannot be left blank") }]}
        >
          <Input />
        </Form.Item>

        <Form.Item className="font-bold" label={t("Last Name")} name="lastName">
          <Input />
        </Form.Item>

        <Form.Item className="font-bold" label={t("Language")} name="language">
          <Select
            options={[
              { value: "en", label: t("English") },
              { value: "vn", label: t("Vietnamese") },
            ]}
          />
        </Form.Item>

        <Form.Item
          className="font-bold"
          label={t("Password")}
          name="password"
          rules={[
            { required: true, message: t("Password cannot be left blank") },
            { min: 6, message: t("Password must have at least 6 characters") },
          ]}
        >
          <Input.Password autoComplete="current-password" />
        </Form.Item>

        <Form.Item className="font-bold" label={t("Role")} name="role">
          <Select
            options={roles.map((role: any) => ({
              value: role.id,
              label: t(role.name),
            }))}
          />
        </Form.Item>
        <Form.Item className="flex justify-center w-[448px] ml-[128px]  mt-5">
          <Button type="primary" htmlType="submit" className="w-[150px] text-base">
            {editMode ? t("Edit") : t("Create")}
          </Button>
        </Form.Item>

        {/* <Form.Item
          className="font-bold "
          label="Send User Notification"
          name="emailSubscription"
          valuePropName="checked"
        >
          <Checkbox className="ml-[30px] font-normal">Send the new user an email about their account</Checkbox>
        </Form.Item> */}
      </Form>
    </>
  )
}

export default UpsertForm
